import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Modal, Switch, Input, Button, OtpCodeInputGroup } from "../common";
import { useGoogleLogin } from "@react-oauth/google";
import {
  loginByEmail,
  loginByGoogle,
  registerByEmail,
  registerByGoogle,
  verifyEmail,
  resendEmailVerificationCode,
  forgotPasswordSendVerification,
  resetPassword,
} from "../../lib/scripts/auth.scripts";
import { setAuthToken } from "../../lib/fetchInstance";
import { useUser } from "./UserProvider";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";

const AuthenticateContext = createContext(undefined);

export const useAuthenticate = () => {
  const context = useContext(AuthenticateContext);

  if (!context) {
    throw new Error("useAuthenticate must be within AuthenticateProvider");
  }

  return context;
};

export const AuthenticateProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExternal, setIsExternal] = useState(false);
  const [activedAuthIndex, setActivedAuthIndex] = useState(0);
  const [activedWalletIndex, setActivedWalletIndex] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [checkPassword, setCheckPassword] = useState("");
  const [isInvalidName, setIsInvalidName] = useState(false);
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);
  const [isInvalidPassword, setIsInvalidPassword] = useState(false);
  const [isInvalidCheckPassword, setIsInvalidCheckPassword] = useState(false);
  const [isInvalidWalletAddress, setIsInvalidWalletAddress] = useState(false);
  const [invalidPasswordText, setInvalidPasswordText] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isEmailVerifyOpen, setIsEmailVerifyOpen] = useState(false);
  const [code, setCode] = useState("");
  const [isInvalidCode, setIsInvalidCode] = useState(false);
  const [invalidCodeText, setInvalidCodeText] = useState(
    "OTP must be 6 letters"
  );
  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const [isSentForgotVerification, setIsSentVerification] = useState(false);
  const [remainingTime, setRemainingTime] = useState(180);
  const timerRef = useRef(null);

  const { setIsAuthenticated, setUser, user } = useUser();

  const switchAuthItems = ["Log in", "Register"];
  const switchWalletItems = ["Temporary Wallet", "My Wallet"];

  const onOpen = () => setIsOpen(true);

  const onClose = () => setIsOpen(false);

  const onEmailVerifyOpen = () => setIsEmailVerifyOpen(true);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidBnbAddress = /^0x[a-fA-F0-9]{40}$/.test(walletAddress.trim());

  const isValid = () => {
    if (activedAuthIndex === 0) {
      if (!validateEmail(email)) {
        setIsInvalidEmail(true);
        return false;
      } else {
        setIsInvalidEmail(false);
      }
      if (password.trim().length === 0) {
        setInvalidPasswordText("Wrong password format");
        setIsInvalidPassword(true);
        return false;
      } else {
        setIsInvalidPassword(false);
      }
      return true;
    } else {
      if (name.trim().length === 0) {
        setIsInvalidName(true);
        return false;
      } else {
        setIsInvalidName(false);
      }
      if (!validateEmail(email)) {
        setIsInvalidEmail(true);
        return false;
      } else {
        setIsInvalidEmail(false);
      }
      if (password.trim().length === 0) {
        setInvalidPasswordText("Wrong password format");
        return false;
      }
      if (checkPassword.trim().length === 0) {
        setIsInvalidCheckPassword(true);
        return false;
      }
      if (password !== checkPassword) {
        setInvalidPasswordText("Password is not matching");
        setIsInvalidPassword(true);
        return false;
      }
      setIsInvalidPassword(false);
      setIsInvalidCheckPassword(false);
    }

    if (activedAuthIndex === 1 && activedWalletIndex === 1) {
      if (walletAddress.trim().length === 0 || !isValidBnbAddress) {
        setIsInvalidWalletAddress(true);
        return false;
      }
    }
    return true;
  };

  const handleGoogleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (codeResponse) => {
      try {
        setGoogleLoading(true);
        const { code } = codeResponse;
        let response;

        if (activedAuthIndex === 0) {
          response = await loginByGoogle(code);
        } else {
          if (activedAuthIndex === 1 && activedWalletIndex === 1) {
            if (walletAddress.trim().length === 0 || !isValidBnbAddress) {
              setIsInvalidWalletAddress(true);
              setGoogleLoading(false);
              return;
            } else {
              setIsInvalidWalletAddress(false);
            }
          }
          response = await registerByGoogle(code, walletAddress);
        }

        if (response.ok) {
          const { token, user } = response.data;
          setAuthToken(token);
          setIsAuthenticated(true);
          setUser(user);
          if (activedAuthIndex === 1) {
            toast.success("Welcome !!!");
          } else if (activedAuthIndex === 0) {
            toast.success("Welcome back !!!");
          }
          onClose();
          onEmailVerifyOpen();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        console.error("Authentication failed:", error);
        toast.error("Something went wrong");
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error("Google Login Error:", error);
    },
  });

  const handleEmailRegister = async () => {
    if (!isValid()) return;

    try {
      setEmailLoading(true);

      const response = await registerByEmail(
        name,
        email,
        password,
        walletAddress
      );

      if (response.ok) {
        const { token, user } = response.data;
        setAuthToken(token);
        setIsAuthenticated(true);
        setUser(user);
        toast.success("Email verification OTP was sent to your email");
        onClose();
        onEmailVerifyOpen();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("email register error: ", error);
      toast.error("Something went wrong");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailLogin = async () => {
    if (!isValid()) return;

    try {
      setEmailLoading(true);

      const response = await loginByEmail(email, password);

      if (response.ok) {
        const { token, user } = response.data;
        setAuthToken(token);
        setIsAuthenticated(true);
        setUser(user);
        toast.success("Welcome !!!");
        onClose();
        console.log(user);
        onEmailVerifyOpen();
      } else {
        if (response.reason) {
          setIsInvalidPassword(true);
          setInvalidPasswordText(
            response.reason === "google"
              ? "Signed with Google"
              : "Incorrect password"
          );
        }
        toast.error(response.message);
      }
    } catch (error) {
      console.error("email login error: ", error);
      toast.error("Something went wrong");
    } finally {
      setEmailLoading(false);
    }
  };

  const onLogout = () => {
    setAuthToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  // Timer effect that starts when email verification modal opens
  useEffect(() => {
    if (isEmailVerifyOpen && !isExternal) {
      // Reset timer to 180 seconds when modal opens
      setRemainingTime(180);

      // Start the countdown timer immediately
      timerRef.current = setInterval(() => {
        setRemainingTime((prevTime) => {
          if (prevTime <= 1) {
            // Timer expired
            clearInterval(timerRef.current);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    } else {
      // Clear timer when modal closes
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    // Cleanup timer on component unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isEmailVerifyOpen, isExternal]);

  useEffect(() => {
    if (isOpen) {
      // Reset all states when modal opens
      setActivedAuthIndex(0);
      setActivedWalletIndex(0);
      setName("");
      setEmail("");
      setPassword("");
      setCheckPassword("");
      setIsInvalidName(false);
      setIsInvalidEmail(false);
      setIsInvalidPassword(false);
      setIsInvalidCheckPassword(false);
      setIsInvalidWalletAddress(false);
      setInvalidPasswordText("");
      setWalletAddress("");
      setCode("");
      setIsInvalidCode(false);
      setInvalidCodeText("OTP must be 6 letters");
      setRemainingTime(180);

      // Clear existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isForgotOpen) {
      setEmail("");
      setCode("");
      setPassword("");
      setIsInvalidEmail(false);
      setIsInvalidCode(false);
      setIsInvalidPassword(false);
      setRemainingTime(180);

      // Start the countdown timer immediately
      timerRef.current = setInterval(() => {
        setRemainingTime((prevTime) => {
          if (prevTime <= 1) {
            // Timer expired
            clearInterval(timerRef.current);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
  }, [isForgotOpen]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleSubmitEmailVerify = async () => {
    if (!user?.email) return;
    if (code.length !== 6) {
      setIsInvalidCode(true);
      return;
    }

    try {
      setSubmitLoading(true);
      const response = await verifyEmail(code, user.email);

      if (response.ok) {
        const { user } = response.data;
        setUser(user);
        setIsEmailVerifyOpen(false);
        toast.success("Verified");
      } else {
        if (response.reason) {
          setIsInvalidCode(true);
          setInvalidCodeText(response.message);
          setSubmitLoading(false);
          return;
        } else {
          toast.error(response.message);
        }
      }
    } catch (error) {
      console.error("handle submit email verify error: ", error);
      toast.error("Something went wrong");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!user?.email) return;

    setIsInvalidCode(false);

    try {
      setResendLoading(true);
      const response = await resendEmailVerificationCode(user.email);

      if (response.ok) {
        const { user } = response.data;
        setUser(user);
        toast.success("Verification code sent!");
        // Reset timer to 180 seconds when resending
        setRemainingTime(180);

        // Clear existing timer and start new one
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }

        timerRef.current = setInterval(() => {
          setRemainingTime((prevTime) => {
            if (prevTime <= 1) {
              clearInterval(timerRef.current);
              return 0;
            }
            return prevTime - 1;
          });
        }, 1000);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("resend email verification code error: ", error);
      toast.error("Something went wrong");
    } finally {
      setResendLoading(false);
    }
  };

  const handleForgotPasswordSendVerify = async () => {
    if (email.trim().length === 0 || !validateEmail(email)) {
      setIsInvalidEmail(true);
      return;
    }

    try {
      setSubmitLoading(true);
      const response = await forgotPasswordSendVerification(email);

      if (response.ok) {
        const { user } = response.data;
        setUser(user);
        toast.success(response.message);
        setIsSentVerification(true);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("handle forgot password send verify error: ", error);
      toast.error("Something went wrong");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user) {
      toast.error("Please refresh the page");
      return;
    }

    if (code.length !== 6) {
      setInvalidCodeText("OTP must be 6 letters");
      setIsInvalidCode(true);
      return;
    }

    if (password.trim().length === 0) {
      setInvalidPasswordText("Wrong password format");
      setIsInvalidPassword(true);
      return;
    }

    try {
      setSubmitLoading(true);

      const response = await resetPassword(code, user.email, password);

      if (response.ok) {
        const { user } = response.data;
        setUser(user);
        setIsForgotOpen(false);
        setIsInvalidPassword(false);
        setInvalidPasswordText("");
        setIsInvalidEmail(false);
        toast.success("Password reset successfully");
      } else {
        if (response.reason) {
          setIsInvalidCode(true);
          setInvalidCodeText(response.message);
        } else {
          toast.error(response.message);
        }
      }
    } catch (error) {
      console.error("handle reset password error: ", error);
      toast.error("Something went wrong");
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (isExternal) {
      setRemainingTime(0);
    }
  }, [isExternal]);

  return (
    <AuthenticateContext.Provider
      value={{
        isOpen,
        onOpen,
        onClose,
        onLogout,
        onEmailVerifyOpen,
        setIsExternal,
      }}
    >
      {children}

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        title="Authenticate"
        height={activedAuthIndex === 0 ? 50 : 63}
      >
        <div className="w-full space-y-4 flex flex-col items-center justify-center">
          <Switch
            items={switchAuthItems}
            activedIndex={activedAuthIndex}
            onClick={setActivedAuthIndex}
            size="sm"
          />

          <div className="w-full space-y-2">
            {activedAuthIndex === 1 && (
              <Input
                type="text"
                name="username"
                placeholder="Full name"
                icon="solar:user-bold-duotone"
                value={name}
                isInvalid={isInvalidName}
                invalidText="Wrong full name format"
                onChange={setName}
              />
            )}
            <Input
              type="email"
              name="email"
              icon="solar:inbox-line-bold-duotone"
              placeholder="Email"
              value={email}
              invalidText="Wrong email format (e.g user@email.com)"
              isInvalid={isInvalidEmail}
              onChange={setEmail}
            />
            <Input
              type="password"
              name="password"
              icon="solar:lock-bold-duotone"
              placeholder="Password"
              invalidText={invalidPasswordText}
              isInvalid={isInvalidPassword}
              value={password}
              onChange={setPassword}
            />
            {activedAuthIndex === 0 && (
              <button
                className="text-white font-semibold text-xs underline cursor-pointer hover:text-green-500"
                onClick={() => setIsForgotOpen(true)}
              >
                Forgot password
              </button>
            )}
            {activedAuthIndex === 1 && (
              <Input
                type="password"
                name="checkpassword"
                icon="solar:check-circle-bold-duotone"
                placeholder="Check password"
                invalidText="Password is not matching"
                isInvalid={isInvalidCheckPassword}
                value={checkPassword}
                onChange={setCheckPassword}
              />
            )}
          </div>

          {activedAuthIndex === 1 && (
            <>
              <div className="w-full">
                <p className="text-green-500 font-semibold text-xs">
                  Your wallet will be temporarily created for this presale.
                  We’ll keep your wallet ownership and personal data secure.
                </p>
              </div>
              {/* <Switch
                items={switchWalletItems}
                activedIndex={activedWalletIndex}
                onClick={setActivedWalletIndex}
                size="sm"
              />

              <div className="w-full">
                {activedWalletIndex === 0 ? (
                  <p className="text-green-500 font-semibold text-xs px-1">
                    Your wallet will be temporarily created for this presale
                    <br />
                    We’ll keep your wallet ownership and personal data secure
                  </p>
                ) : (
                  <div className="w-full space-y-1">
                    <Input
                      type="text"
                      placeholder="BNB wallet address (e.g 0x...)"
                      icon="solar:wallet-bold-duotone"
                      value={walletAddress}
                      invalidText="Wrong BNB wallet address format (e.g 0x...)"
                      isInvalid={isInvalidWalletAddress}
                      onChange={setWalletAddress}
                    />
                    <p className="text-green-500 font-semibold text-xs px-1">
                      All CHRLE tokens will be sent to this wallet address.
                      <br />
                      Please make sure it’s correct before proceeding.
                    </p>
                  </div>
                )}
              </div> */}
            </>
          )}

          <div className="w-full mt-6">
            <Button
              type="primary"
              label={
                activedAuthIndex === 0
                  ? "Log in with Email"
                  : "Register with Email"
              }
              loading={emailLoading}
              onClick={
                activedAuthIndex === 0 ? handleEmailLogin : handleEmailRegister
              }
            />
          </div>

          {/* Divider */}
          <div className="w-full flex items-center gap-4 justify-between">
            <div className="w-full bg-white/20 h-[1px]"></div>
            <span className="text-white/40 font-normal">OR</span>
            <div className="w-full bg-white/20 h-[1px]"></div>
          </div>

          {/* Social Login/Register */}
          <div className="w-full">
            <Button
              type="secondary"
              label={`${
                activedAuthIndex === 0 ? "Log in" : "Register"
              } with Google`}
              icon="fa-brands:google"
              loading={googleLoading}
              onClick={handleGoogleLogin}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="Email Verification"
        isOpen={isEmailVerifyOpen}
        size="md"
        height={40}
      >
        {user && (
          <div className="w-full space-y-4">
            <p className="text-xs text-green-500 font-semibold">
              We have sent OTP to {user.email}
            </p>
            <div className="w-full space-y-2">
              <OtpCodeInputGroup
                value={code}
                invalidText={invalidCodeText}
                isInvalid={isInvalidCode}
                onChange={setCode}
              />
              <p className="text-white/80 text-sm">
                OTP will expire in {formatTime(remainingTime)}
                {remainingTime === 0 && !isExternal && (
                  <span className="text-red-500 ml-2 font-semibold">
                    (Expired)
                  </span>
                )}
              </p>
            </div>
            <div className="w-full space-y-4">
              <Button
                type="primary"
                label="Submit"
                loading={submitLoading}
                onClick={handleSubmitEmailVerify}
              />
              <div className="w-full flex items-center justify-center gap-2">
                <span className="text-red-500 text-xs font-semibold">
                  Didn't get verification code?
                </span>
                <button
                  className="text-xs text-white font-semibold underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  onClick={handleResendCode}
                  disabled={remainingTime > 0}
                >
                  Resend {remainingTime > 0 && `(${formatTime(remainingTime)})`}
                  {resendLoading && (
                    <Icon
                      icon="svg-spinners:6-dots-scale-middle"
                      className="w-4 h-4 text-white"
                    />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Reset password"
        isOpen={isForgotOpen}
        onClose={() => setIsForgotOpen(false)}
        size="md"
        height={isSentForgotVerification ? 43 : 30}
      >
        {!isSentForgotVerification ? (
          <div className="w-full space-y-4">
            <p className="text-xs text-green-500 font-semibold">
              We will send a verification code to your email
            </p>
            <Input
              type="email"
              name="email"
              icon="solar:inbox-line-bold-duotone"
              placeholder="Email"
              value={email}
              invalidText="Wrong email format (e.g user@email.com)"
              isInvalid={isInvalidEmail}
              onChange={setEmail}
            />
            <Button
              type="primary"
              label="Submit"
              loading={submitLoading}
              onClick={handleForgotPasswordSendVerify}
            />
          </div>
        ) : (
          <div className="w-full space-y-4">
            <p className="text-xs text-green-500 font-semibold">
              We have sent OTP to {user?.email}
            </p>
            <div className="w-full space-y-2">
              <OtpCodeInputGroup
                value={code}
                invalidText={invalidCodeText}
                isInvalid={isInvalidCode}
                onChange={setCode}
              />
              <p className="text-white/80 text-sm">
                OTP will expire in {formatTime(remainingTime)}
                {remainingTime === 0 && !isExternal && (
                  <span className="text-red-500 ml-2 font-semibold">
                    (Expired)
                  </span>
                )}
              </p>
            </div>
            <Input
              type="password"
              name="newpassword"
              icon="solar:lock-bold-duotone"
              placeholder="New password"
              invalidText={invalidPasswordText}
              isInvalid={isInvalidPassword}
              value={password}
              onChange={setPassword}
            />
            <div className="w-full space-y-4">
              <Button
                type="primary"
                label="Submit"
                loading={submitLoading}
                onClick={handleResetPassword}
              />
              <div className="w-full flex items-center justify-center gap-2">
                <span className="text-red-500 text-xs font-semibold">
                  Didn't get verification code?
                </span>
                <button
                  className="text-xs text-white font-semibold underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  onClick={handleResendCode}
                  disabled={remainingTime > 0}
                >
                  Resend {remainingTime > 0 && `(${formatTime(remainingTime)})`}
                  {resendLoading && (
                    <Icon
                      icon="svg-spinners:6-dots-scale-middle"
                      className="w-4 h-4 text-white"
                    />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </AuthenticateContext.Provider>
  );
};
